/* tslint:disable */
/* eslint-disable */
/* Mocked in-memory implementation of UsersClient for local development and UI wiring. */

import { CancelToken } from 'axios';
import {
  SuccessResponse_1OfUserListResponse,
  SuccessResponse_1OfUser,
  SuccessResponse_1OfList_1OfUser,
  CreateUserRequestDto,
  ActivateUserRequestDto,
  MigrateUserRequestDto,
  MigrateUserResponseDto,
  AuthRequirementsResponseDto,
  PutUserRequest,
  ResetPasswordRequestDto,
  ImportUserRequest,
  User,
  Roles,
  Franchise,
  System,
  Store,
  Persona,
} from '../../types';

/* ---- Helper Functions ---- */

function delay(ms: number = 300): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function successResponse<T>(data: T): { data: T; success: boolean; message: string } {
  return { data, success: true, message: 'Success' };
}

/* ---- Mock Data ---- */

const mockFranchises: Franchise[] = [
  {
    franchiseId: 1,
    tenantId: 1,
    franchiseName: 'Burger Palace',
    franchiseUUID: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
    active: true,
    currentBrandingId: 1,
    systems: [
      { id: 0, name: 'Orderly for...', description: '', modules: [] },
      { id: 1, name: 'Orderly for Stores', description: '', modules: [] },
    ],
  },
  {
    franchiseId: 2,
    tenantId: 2,
    franchiseName: 'Global Foods Distribution',
    franchiseUUID: 'b2c3d4e5-f6a7-8901-bcde-f23456789012',
    active: true,
    currentBrandingId: 2,
    systems: [
      { id: 0, name: 'Orderly for...', description: '', modules: [] },
      { id: 2, name: 'Orderly for Distributors', description: '', modules: [] },
    ],
  },
  {
    franchiseId: 10,
    tenantId: 10,
    franchiseName: 'Vapiano',
    franchiseUUID: 'c3d4e5f6-a7b8-9012-cdef-345678901234',
    active: true,
    currentBrandingId: 3,
    systems: [
      { id: 0, name: 'Orderly for...', description: '', modules: [] },
      { id: 1, name: 'Orderly for Stores', description: '', modules: [] },
      { id: 2, name: 'Orderly for Distributors', description: '', modules: [] },
    ],
  },
  {
    franchiseId: 11,
    tenantId: 11,
    franchiseName: 'Five Guys',
    franchiseUUID: 'd4e5f6a7-b8c9-0123-defa-456789012345',
    active: true,
    currentBrandingId: 4,
    systems: [
      { id: 0, name: 'Orderly for...', description: '', modules: [] },
      { id: 1, name: 'Orderly for Stores', description: '', modules: [] },
      { id: 2, name: 'Orderly for Distributors', description: '', modules: [] },
    ],
  },
];

const mockSystems: System[] = [
  {
    id: 2,
    name: 'Orderly for Distributors',
    description: '',
    modules: [
      {
        id: 3,
        name: 'Catalogues',
        description: '',
        premium: false,
        permissions: [
          { id: 121, name: 'CataloguesView', description: null, isVisible: false },
          { id: 122, name: 'CataloguesDetailView', description: null, isVisible: false },
          { id: 160, name: 'CataloguesExport', description: null, isVisible: false },
          { id: 161, name: 'CataloguesImport', description: null, isVisible: false },
        ],
      },
      {
        id: 4,
        name: 'Retailers',
        description: '',
        premium: false,
        permissions: [
          { id: 128, name: 'RetailersView', description: null, isVisible: false },
          { id: 129, name: 'RetailersCreate', description: null, isVisible: false },
          { id: 130, name: 'RetailersDelete', description: null, isVisible: false },
          { id: 131, name: 'RetailersDetailsView', description: null, isVisible: false },
          { id: 132, name: 'RetailersDetailsSave', description: null, isVisible: false },
          { id: 133, name: 'RetailersDetailsApprove', description: null, isVisible: false },
          { id: 134, name: 'RetailersDetailsLocationAdd', description: null, isVisible: false },
          { id: 135, name: 'RetailersDetailsLocationDelete', description: null, isVisible: false },
          { id: 136, name: 'RetailersDetailsDocumentationUpload', description: null, isVisible: false },
          { id: 137, name: 'RetailersDetailsDocumentationDownload', description: null, isVisible: false },
          { id: 138, name: 'RetailersDetailsDocumentationDelete', description: null, isVisible: false },
          { id: 139, name: 'RetailersDetailsDocumentationVerify', description: null, isVisible: false },
          { id: 159, name: 'RetailersViewOtherRegion', description: null, isVisible: false },
        ],
      },
      {
        id: 5,
        name: 'Dashboard',
        description: '',
        premium: false,
        permissions: [
          { id: 95, name: 'ViewDailyTasks', description: null, isVisible: false },
          { id: 96, name: 'ViewOrderTasks', description: null, isVisible: false },
          { id: 97, name: 'ViewRetailerTasks', description: null, isVisible: false },
          { id: 98, name: 'ViewOrdersStatusPanel', description: null, isVisible: false },
          { id: 99, name: 'ViewOrdersOverTime', description: null, isVisible: false },
          { id: 100, name: 'ViewOrdersByMarket', description: null, isVisible: false },
          { id: 101, name: 'ViewSalesByStatus', description: null, isVisible: false },
          { id: 102, name: 'ViewSalesOverTime', description: null, isVisible: false },
          { id: 103, name: 'ViewSalesByMarket', description: null, isVisible: false },
          { id: 104, name: 'ViewRetailersByOnboardingStatus', description: null, isVisible: false },
          { id: 105, name: 'ViewStaleRetailers', description: null, isVisible: false },
          { id: 106, name: 'ViewRetailersByValue', description: null, isVisible: false },
          { id: 107, name: 'ViewTopSellingProductsByValue', description: null, isVisible: false },
          { id: 108, name: 'ViewTopSellingProductsByMarket', description: null, isVisible: false },
          { id: 109, name: 'ViewWorstPerformingProducts', description: null, isVisible: false },
          { id: 110, name: 'ViewDeliveriesByStatus', description: null, isVisible: false },
          { id: 111, name: 'ViewDeliveriesOverTime', description: null, isVisible: false },
          { id: 112, name: 'ViewDeliveriesByMarket', description: null, isVisible: false },
          { id: 113, name: 'ViewDeliveredInFull', description: null, isVisible: false },
          { id: 114, name: 'ViewDeliveredOnTime', description: null, isVisible: false },
          { id: 115, name: 'ViewCreditsClaimsByValue', description: null, isVisible: false },
          { id: 116, name: 'ViewCreditsClaimsByQuantity', description: null, isVisible: false },
          { id: 117, name: 'ViewCreditsClaimsByMarket', description: null, isVisible: false },
          { id: 118, name: 'ViewCreditsClaimsByStatus', description: null, isVisible: false },
          { id: 119, name: 'ViewInvoicesByStatus', description: null, isVisible: false },
          { id: 120, name: 'ViewInvoicesByValue', description: null, isVisible: false },
        ],
      },
      {
        id: 6,
        name: 'Orders',
        description: '',
        premium: false,
        permissions: [
          { id: 123, name: 'ProductDetailView', description: null, isVisible: false },
          { id: 140, name: 'OrdersView', description: null, isVisible: false },
          { id: 141, name: 'OrdersCreate', description: null, isVisible: false },
          { id: 142, name: 'OrdersCopy', description: null, isVisible: false },
          { id: 143, name: 'OrdersApprove', description: null, isVisible: false },
          { id: 157, name: 'OrdersApproveConfirmation', description: null, isVisible: false },
          { id: 158, name: 'OrdersChangeDeliveryDate', description: null, isVisible: false },
          { id: 162, name: 'OrdersRejectConfirmation', description: null, isVisible: false },
          { id: 163, name: 'OrdersDelete', description: null, isVisible: false },
          { id: 208, name: 'PriceOverrideWrite', description: null, isVisible: false },
          { id: 209, name: 'DeliveryChargeWrite', description: null, isVisible: false },
          { id: 210, name: 'DeliveryMethodWrite', description: null, isVisible: false },
          { id: 211, name: 'DeliveryMethodView', description: null, isVisible: false },
        ],
      },
      {
        id: 7,
        name: 'Deliveries',
        description: '',
        premium: false,
        permissions: [
          { id: 145, name: 'DeliveriesView', description: null, isVisible: false },
        ],
      },
      {
        id: 9,
        name: 'Finance',
        description: '',
        premium: false,
        permissions: [
          { id: 146, name: 'FinanceView', description: null, isVisible: false },
        ],
      },
      {
        id: 10,
        name: 'Reports',
        description: '',
        premium: false,
        permissions: [
          { id: 144, name: 'ReportsView', description: null, isVisible: false },
        ],
      },
      {
        id: 32,
        name: 'Product Details',
        description: '',
        premium: false,
        permissions: [
          { id: 123, name: 'ProductDetailView', description: null, isVisible: false },
          { id: 124, name: 'ProductDetailExportAttributesEdit', description: null, isVisible: false },
          { id: 125, name: 'ProductDetailDocumentsDownload', description: null, isVisible: false },
          { id: 126, name: 'ProductDetailDocumentsUpload', description: null, isVisible: false },
          { id: 127, name: 'ProductDetailDocumentsDelete', description: null, isVisible: false },
        ],
      },
    ],
  },
  {
    id: 1,
    name: 'Orderly for Stores',
    description: '',
    modules: [
      { id: 11, name: 'Home', description: 'Storefront home navigation', premium: false, permissions: [] },
      {
        id: 12,
        name: 'Order Stock',
        description: 'Place and manage orders for stock',
        premium: false,
        permissions: [
          { id: 55, name: 'OrderAuditRead', description: null, isVisible: false },
          { id: 56, name: 'OrderAdHocOrders', description: null, isVisible: false },
          { id: 164, name: 'OfsOrdersView', description: null, isVisible: false },
          { id: 165, name: 'OfsOrdersCreate', description: null, isVisible: false },
          { id: 166, name: 'OfsOrdersDelete', description: null, isVisible: false },
        ],
      },
      {
        id: 13,
        name: 'Receive Stock',
        description: 'Receive stock and reconcile invoices',
        premium: false,
        permissions: [
          { id: 8, name: 'PurchaseOrderRead', description: null, isVisible: false },
          { id: 9, name: 'PurchaseOrderWrite', description: null, isVisible: false },
          { id: 31, name: 'InvoiceReconciliationRead', description: null, isVisible: false },
          { id: 32, name: 'InvoiceReconciliationWrite', description: null, isVisible: false },
          { id: 33, name: 'InvoiceReconciliationAdmin', description: null, isVisible: false },
          { id: 39, name: 'PurchaseOrderNotCompletedEmail', description: null, isVisible: false },
          { id: 40, name: 'PurchaseOrderNotCompletedSummaryEmail', description: null, isVisible: false },
          { id: 43, name: 'PurchaseOrderAdjustmentsWrite', description: null, isVisible: false },
        ],
      },
      {
        id: 14,
        name: 'Transfer Stock',
        description: 'Manage internal stock transfers',
        premium: false,
        permissions: [
          { id: 28, name: 'TransferRead', description: null, isVisible: false },
        ],
      },
      {
        id: 15,
        name: 'Waste Stock',
        description: 'Record daily stock waste',
        premium: false,
        permissions: [
          { id: 44, name: 'DailyWasteCheckSheetRead', description: null, isVisible: false },
          { id: 45, name: 'DailyWasteCheckSheetWrite', description: null, isVisible: false },
        ],
      },
      {
        id: 16,
        name: 'Count Stock',
        description: 'Perform and review stock counts',
        premium: false,
        permissions: [
          { id: 16, name: 'StockCountRead', description: null, isVisible: false },
          { id: 17, name: 'StockCountWrite', description: null, isVisible: false },
          { id: 18, name: 'StockLocationRead', description: null, isVisible: false },
          { id: 24, name: 'StockCountAdmin', description: null, isVisible: false },
          { id: 34, name: 'StockCountEmail', description: null, isVisible: false },
          { id: 36, name: 'StockCountDelete', description: null, isVisible: false },
          { id: 37, name: 'StockCountNotCompletedEmail', description: null, isVisible: false },
          { id: 38, name: 'StockCountNotCompletedSummaryEmail', description: null, isVisible: false },
        ],
      },
      {
        id: 17,
        name: 'Transactions',
        description: 'View and manage transactions',
        premium: false,
        permissions: [
          { id: 10, name: 'TransactionRead', description: null, isVisible: false },
          { id: 11, name: 'TransactionWrite', description: null, isVisible: false },
          { id: 12, name: 'TransactionAdmin', description: null, isVisible: false },
          { id: 207, name: 'TransactionDetailsRead', description: null, isVisible: false },
        ],
      },
      {
        id: 18,
        name: 'Recipes',
        description: 'Browse and update recipes',
        premium: false,
        permissions: [
          { id: 4, name: 'RecipeRead', description: null, isVisible: false },
          { id: 5, name: 'RecipeWrite', description: null, isVisible: false },
          { id: 6, name: 'RecipeAdmin', description: null, isVisible: false },
          { id: 7, name: 'ConversionRead', description: null, isVisible: false },
          { id: 46, name: 'MadeInStoreRead', description: null, isVisible: false },
          { id: 47, name: 'MadeInStoreWrite', description: null, isVisible: false },
          { id: 48, name: 'CreateItemsAndRecipes', description: null, isVisible: false },
          { id: 167, name: 'RecipeDetailsRead', description: null, isVisible: false },
        ],
      },
      {
        id: 19,
        name: 'Items',
        description: 'Browse items and unit conversions',
        premium: false,
        permissions: [
          { id: 4, name: 'RecipeRead', description: null, isVisible: false },
          { id: 7, name: 'ConversionRead', description: null, isVisible: false },
          { id: 14, name: 'StockWrite', description: null, isVisible: false },
          { id: 25, name: 'ItemRead', description: null, isVisible: false },
        ],
      },
      {
        id: 20,
        name: 'Stock on Hand',
        description: 'View current stock levels',
        premium: false,
        permissions: [
          { id: 13, name: 'StockRead', description: null, isVisible: false },
        ],
      },
      {
        id: 21,
        name: 'Reporting',
        description: 'Access reporting modules',
        premium: false,
        permissions: [
          { id: 26, name: 'ReportRead', description: null, isVisible: false },
        ],
      },
      {
        id: 23,
        name: 'Stock Count Schedule',
        description: 'Schedule and configure stock count events',
        premium: false,
        permissions: [
          { id: 16, name: 'StockCountRead', description: null, isVisible: false },
          { id: 18, name: 'StockLocationRead', description: null, isVisible: false },
          { id: 19, name: 'StockLocationWrite', description: null, isVisible: false },
          { id: 20, name: 'StockListRead', description: null, isVisible: false },
          { id: 22, name: 'StockCountScheduleRead', description: null, isVisible: false },
          { id: 23, name: 'StockCountScheduleWrite', description: null, isVisible: false },
          { id: 24, name: 'StockCountAdmin', description: null, isVisible: false },
          { id: 52, name: 'StockCountUomsRead', description: null, isVisible: false },
          { id: 53, name: 'StockCountUomsWrite', description: null, isVisible: false },
          { id: 58, name: 'StockCountApprove', description: null, isVisible: false },
        ],
      },
      {
        id: 24,
        name: 'Manage Transactions',
        description: 'Administer transaction-related records',
        premium: false,
        permissions: [
          { id: 12, name: 'TransactionAdmin', description: null, isVisible: false },
          { id: 15, name: 'SourceTransactionRead', description: null, isVisible: false },
          { id: 27, name: 'TransferWrite', description: null, isVisible: false },
          { id: 49, name: 'ManualTransactionCostPriceWrite', description: null, isVisible: false },
          { id: 50, name: 'IncomingTransferWrite', description: null, isVisible: false },
          { id: 51, name: 'OutgoingTransferWrite', description: null, isVisible: false },
        ],
      },
      {
        id: 28,
        name: 'Category Management',
        description: 'Maintain categories',
        premium: false,
        permissions: [
          { id: 30, name: 'CategoryAdmin', description: null, isVisible: false },
        ],
      },
      {
        id: 29,
        name: 'Predictive Ordering Settings',
        description: 'Configure predictive ordering (AI POD)',
        premium: true,
        permissions: [
          { id: 54, name: 'AIOrderingAdmin', description: null, isVisible: false },
        ],
      },
      { id: 31, name: 'Support', description: 'Access system support', premium: false, permissions: [] },
    ],
  },
  {
    id: 0,
    name: 'Orderly for...',
    description: '',
    modules: [
      {
        id: 22,
        name: 'Role Management',
        description: 'Manage roles and permissions',
        premium: false,
        permissions: [
          { id: 2, name: 'RolesAdmin', description: null, isVisible: false },
          { id: 148, name: 'RoleManagementView', description: null, isVisible: false },
          { id: 149, name: 'RoleManagementCreate', description: null, isVisible: false },
          { id: 150, name: 'RoleManagementUpdate', description: null, isVisible: false },
          { id: 151, name: 'RoleManagementDelete', description: null, isVisible: false },
        ],
      },
      {
        id: 25,
        name: 'Scheduled Reports',
        description: 'View or configure automated reports',
        premium: true,
        permissions: [
          { id: 35, name: 'AutomatedTasks', description: null, isVisible: false },
        ],
      },
      {
        id: 26,
        name: 'User Management',
        description: 'Administer system users',
        premium: false,
        permissions: [
          { id: 1, name: 'UserAdmin', description: null, isVisible: false },
          { id: 152, name: 'UserManagementView', description: null, isVisible: false },
          { id: 153, name: 'UserManagementCreate', description: null, isVisible: false },
          { id: 154, name: 'UserManagementUpdate', description: null, isVisible: false },
          { id: 155, name: 'UserManagementDelete', description: null, isVisible: false },
        ],
      },
      {
        id: 27,
        name: 'Notifications',
        description: 'Manage notifications',
        premium: false,
        permissions: [
          { id: 29, name: 'NotificationWrite', description: null, isVisible: false },
        ],
      },
      {
        id: 30,
        name: 'Configuration Management',
        description: 'System-level settings and configuration',
        premium: false,
        permissions: [
          { id: 3, name: 'ConfigurationAdmin', description: null, isVisible: false },
          { id: 57, name: 'FranchiseDynamicBrandingWrite', description: null, isVisible: false },
        ],
      },
      {
        id: 33,
        name: 'Instances',
        description: 'Manage system instances',
        premium: false,
        permissions: [
          { id: 168, name: 'InstanceView', description: null, isVisible: false },
          { id: 169, name: 'InstanceCreate', description: null, isVisible: false },
          { id: 170, name: 'InstanceUpdate', description: null, isVisible: false },
        ],
      },
      {
        id: 34,
        name: 'Markets',
        description: 'Manage markets and regions',
        premium: false,
        permissions: [
          { id: 171, name: 'MarketView', description: null, isVisible: false },
          { id: 172, name: 'MarketCreate', description: null, isVisible: false },
          { id: 173, name: 'MarketUpdate', description: null, isVisible: false },
          { id: 174, name: 'MarketDelete', description: null, isVisible: false },
        ],
      },
      {
        id: 35,
        name: 'Order Types',
        description: 'Manage order types and configurations',
        premium: false,
        permissions: [
          { id: 175, name: 'OrderTypeView', description: null, isVisible: false },
          { id: 176, name: 'OrderTypeCreate', description: null, isVisible: false },
          { id: 177, name: 'OrderTypeUpdate', description: null, isVisible: false },
          { id: 178, name: 'OrderTypeDelete', description: null, isVisible: false },
        ],
      },
      {
        id: 36,
        name: 'Suppliers',
        description: 'Manage suppliers and vendor relationships',
        premium: false,
        permissions: [
          { id: 179, name: 'SupplierView', description: null, isVisible: false },
          { id: 180, name: 'SupplierCreate', description: null, isVisible: false },
          { id: 181, name: 'SupplierUpdate', description: null, isVisible: false },
          { id: 182, name: 'SupplierDelete', description: null, isVisible: false },
        ],
      },
      {
        id: 37,
        name: 'Catalogues',
        description: 'Manage product catalogues',
        premium: false,
        permissions: [
          { id: 183, name: 'CatalogueView', description: null, isVisible: false },
          { id: 184, name: 'CatalogueCreate', description: null, isVisible: false },
          { id: 185, name: 'CatalogueUpdate', description: null, isVisible: false },
          { id: 186, name: 'CatalogueDelete', description: null, isVisible: false },
        ],
      },
      {
        id: 38,
        name: 'Stores',
        description: 'Manage stores and locations',
        premium: false,
        permissions: [
          { id: 187, name: 'StoreView', description: null, isVisible: false },
          { id: 188, name: 'StoreCreate', description: null, isVisible: false },
          { id: 189, name: 'StoreUpdate', description: null, isVisible: false },
          { id: 190, name: 'StoreDelete', description: null, isVisible: false },
          { id: 191, name: 'StoreExport', description: null, isVisible: false },
          { id: 192, name: 'StoreImport', description: null, isVisible: false },
        ],
      },
      {
        id: 39,
        name: 'UOM Types',
        description: 'Manage units of measure types',
        premium: false,
        permissions: [
          { id: 193, name: 'UomTypeView', description: null, isVisible: false },
          { id: 194, name: 'UomTypeCreate', description: null, isVisible: false },
          { id: 195, name: 'UomTypeUpdate', description: null, isVisible: false },
          { id: 196, name: 'UomTypeDelete', description: null, isVisible: false },
        ],
      },
      {
        id: 40,
        name: 'Categories',
        description: 'Manage product categories',
        premium: false,
        permissions: [
          { id: 197, name: 'CategoryView', description: null, isVisible: false },
          { id: 198, name: 'CategoryCreate', description: null, isVisible: false },
          { id: 199, name: 'CategoryUpdate', description: null, isVisible: false },
          { id: 200, name: 'CategoryDelete', description: null, isVisible: false },
        ],
      },
      {
        id: 41,
        name: 'Products',
        description: 'Manage products and inventory items',
        premium: false,
        permissions: [
          { id: 201, name: 'ProductView', description: null, isVisible: false },
          { id: 202, name: 'ProductCreate', description: null, isVisible: false },
          { id: 203, name: 'ProductUpdate', description: null, isVisible: false },
          { id: 204, name: 'ProductDelete', description: null, isVisible: false },
          { id: 205, name: 'ProductExport', description: null, isVisible: false },
          { id: 206, name: 'ProductImport', description: null, isVisible: false },
        ],
      },
      {
        id: 42,
        name: 'Autoshipments',
        description: 'Manage autoshipments',
        premium: false,
        permissions: [
          { id: 212, name: 'AutoshipmentRead', description: null, isVisible: false },
          { id: 213, name: 'AutoshipmentWrite', description: null, isVisible: false },
          { id: 214, name: 'AutoshipmentDelete', description: null, isVisible: false },
        ],
      },
      {
        id: 43,
        name: 'Order Delivery Schedules',
        description: 'Manage order delivery schedules',
        premium: false,
        permissions: [
          { id: 215, name: 'ODSRead', description: null, isVisible: false },
          { id: 216, name: 'ODSWrite', description: null, isVisible: false },
          { id: 217, name: 'ODSDelete', description: null, isVisible: false },
        ],
      },
    ],
  },
];

const mockPermissions: (string | null)[] = [
  // Core permissions
  'User', 'Admin', 'UserAdmin', 'RolesAdmin', 'ConfigurationAdmin',
  // Recipe & Item permissions
  'RecipeRead', 'RecipeWrite', 'RecipeAdmin', 'ConversionRead', 'RecipeDetailsRead', 'CreateItemsAndRecipes',
  'MadeInStoreRead', 'MadeInStoreWrite', 'ItemRead',
  // Purchase Order permissions
  'PurchaseOrderRead', 'PurchaseOrderWrite', 'PurchaseOrderAdjustmentsWrite',
  'PurchaseOrderNotCompletedEmail', 'PurchaseOrderNotCompletedSummaryEmail',
  // Transaction permissions
  'TransactionRead', 'TransactionWrite', 'TransactionAdmin', 'SourceTransactionRead', 'TransactionDetailsRead',
  'ManualTransactionCostPriceWrite',
  // Stock permissions
  'StockRead', 'StockWrite', 'StockLocationRead', 'StockLocationWrite',
  'StockListRead', 'StockListWrite',
  // Stock Count permissions
  'StockCountRead', 'StockCountWrite', 'StockCountAdmin', 'StockCountDelete', 'StockCountApprove',
  'StockCountScheduleRead', 'StockCountScheduleWrite', 'StockCountUomsRead', 'StockCountUomsWrite',
  'StockCountEmail', 'StockCountNotCompletedEmail', 'StockCountNotCompletedSummaryEmail',
  // Transfer permissions
  'TransferRead', 'TransferWrite', 'IncomingTransferWrite', 'OutgoingTransferWrite',
  // Invoice & Reconciliation permissions
  'InvoiceReconciliationRead', 'InvoiceReconciliationWrite', 'InvoiceReconciliationAdmin',
  // Order permissions
  'OrdersView', 'OrdersCreate', 'OrdersCopy', 'OrdersApprove', 'OrdersApproveConfirmation',
  'OrdersChangeDeliveryDate', 'OrdersRejectConfirmation', 'OrdersDelete',
  'OfsOrdersView', 'OfsOrdersCreate', 'OfsOrdersDelete',
  'OrderAuditRead', 'OrderAdHocOrders',
  'PriceOverrideWrite', 'DeliveryChargeWrite', 'DeliveryMethodWrite', 'DeliveryMethodView', 'DeliverymethodView',
  // Report permissions
  'ReportRead', 'ReportsView',
  // Notification permissions
  'NotificationWrite',
  // Category permissions
  'CategoryAdmin', 'CategoryView', 'CategoryCreate', 'CategoryUpdate', 'CategoryDelete',
  // Waste permissions
  'DailyWasteCheckSheetRead', 'DailyWasteCheckSheetWrite',
  // AI & Predictive Ordering permissions
  'AIOrderingAdmin', 'AIOrderingStore', 'PredictiveOrdering',
  // Scheduled tasks permissions
  'AutomatedTasks',
  // Branding permissions
  'FranchiseDynamicBrandingWrite',
  // OFD Dashboard permissions
  'ViewDailyTasks', 'ViewOrderTasks', 'ViewRetailerTasks',
  'ViewOrdersStatusPanel', 'ViewOrdersOverTime', 'ViewOrdersByMarket',
  'ViewSalesByStatus', 'ViewSalesOverTime', 'ViewSalesByMarket',
  'ViewRetailersByOnboardingStatus', 'ViewStaleRetailers', 'ViewRetailersByValue',
  'ViewTopSellingProductsByValue', 'ViewTopSellingProductsByMarket', 'ViewWorstPerformingProducts',
  'ViewDeliveriesByStatus', 'ViewDeliveriesOverTime', 'ViewDeliveriesByMarket',
  'ViewDeliveredInFull', 'ViewDeliveredOnTime',
  'ViewCreditsClaimsByValue', 'ViewCreditsClaimsByQuantity', 'ViewCreditsClaimsByMarket', 'ViewCreditsClaimsByStatus',
  'ViewInvoicesByStatus', 'ViewInvoicesByValue',
  // Catalogues permissions
  'CataloguesView', 'CataloguesDetailView', 'CataloguesExport', 'CataloguesImport',
  // Product Detail permissions
  'ProductDetailView', 'ProductDetailExportAttributesEdit',
  'ProductDetailDocumentsDownload', 'ProductDetailDocumentsUpload', 'ProductDetailDocumentsDelete',
  // Retailers permissions
  'RetailersView', 'RetailersCreate', 'RetailersDelete',
  'RetailersDetailsView', 'RetailersDetailsSave', 'RetailersDetailsApprove',
  'RetailersDetailsLocationAdd', 'RetailersDetailsLocationDelete',
  'RetailersDetailsDocumentationUpload', 'RetailersDetailsDocumentationDownload',
  'RetailersDetailsDocumentationDelete', 'RetailersDetailsDocumentationVerify',
  'RetailersViewOtherRegion',
  // Finance & Deliveries permissions
  'DeliveriesView', 'FinanceView',
  // Role Management permissions
  'RoleManagementView', 'RoleManagementCreate', 'RoleManagementUpdate', 'RoleManagementDelete',
  // User Management permissions
  'UserManagementView', 'UserManagementCreate', 'UserManagementUpdate', 'UserManagementDelete',
  // Instance Management permissions
  'InstanceView', 'InstanceCreate', 'InstanceUpdate',
  // Market Management permissions
  'MarketView', 'MarketCreate', 'MarketUpdate', 'MarketDelete',
  // Order Type Management permissions
  'OrderTypeView', 'OrderTypeCreate', 'OrderTypeUpdate', 'OrderTypeDelete',
  // Supplier Management permissions
  'SupplierView', 'SupplierCreate', 'SupplierUpdate', 'SupplierDelete',
  // Catalogue Management permissions
  'CatalogueView', 'CatalogueCreate', 'CatalogueUpdate', 'CatalogueDelete',
  // Store Management permissions
  'StoreView', 'StoreCreate', 'StoreUpdate', 'StoreDelete', 'StoreExport', 'StoreImport',
  // UOM Type Management permissions
  'UomTypeView', 'UomTypeCreate', 'UomTypeUpdate', 'UomTypeDelete',
  // Product Management permissions
  'ProductView', 'ProductCreate', 'ProductUpdate', 'ProductDelete', 'ProductExport', 'ProductImport',
  // Autoshipment permissions
  'AutoshipmentRead', 'AutoshipmentWrite', 'AutoshipmentDelete',
  // Order Delivery Schedules permissions
  'ODSRead', 'ODSWrite', 'ODSDelete',
  // Operational Days permissions
  'OperationalDaysViewDashboard', 'OperationalDaysView', 'OperationalDaysCreate', 'OperationalDaysEdit', 'OperationalDaysDelete',
  // Advanced Tasks permissions
  'AdvancedTasksViewDashboard', 'AdvancedTasksView', 'AdvancedTasksCreate', 'AdvancedTasksReassign',
  'AdvancedTasksRePrioritise', 'AdvancedTasksDelete', 'AdvancedTasksViewEvidence', 'AdvancedTasksViewAudit',
  'AdvancedTasksBulkReassign', 'AdvancedTasksBulkReprioritise',
  // Task Templates permissions
  'TaskTemplatesView', 'TaskTemplatesViewUnrestricted', 'TaskTemplatesCreate', 'TaskTemplatesEdit', 'TaskTemplatesDelete',
  // Task Schedules permissions
  'TaskSchedulesView', 'TaskSchedulesCreate', 'TaskSchedulesEdit', 'TaskSchedulesDelete',
  // Task Categories permissions
  'TaskCategoriesView', 'TaskCategoriesCreate', 'TaskCategoriesEdit', 'TaskCategoriesDelete',
  // Document Management permissions
  'DocumentsView', 'DocumentsViewAll', 'DocumentsUpload', 'DocumentsDownload', 'DocumentsEdit',
  'DocumentsDelete', 'DocumentsCreate', 'DocumentsManagePermissions', 'DocumentsViewEvidence', 'DocumentsUploadEvidence',
  // Automated Workflows permissions
  'AutomatedWorkflowsView', 'AutomatedWorkflowsCreate', 'AutomatedWorkflowsEdit', 'AutomatedWorkflowsDelete',
  // News Management permissions
  'NewsManagementView', 'NewsManagementCreate', 'NewsManagementEdit', 'NewsManagementDelete',
  // Cash Reconciliation permissions
  'CashReconciliationRead', 'CashReconciliationWrite', 'CashReconciliationApprove',
  'CashVarianceOverride', 'TillManagement', 'CashDropManagement', 'CashZoneManagement',
];

const mockStores: Store[] = [
  // Burger Palace (Fast Food) stores
  { storeNumber: '001', starbucksStoreNumber: '001', storeName: 'Burger Palace - Downtown', storeGroup: 'City Centre', legalEntity: '100', franchiseId: 1 },
  { storeNumber: '002', starbucksStoreNumber: '002', storeName: 'Burger Palace - Mall', storeGroup: 'Shopping District', legalEntity: '100', franchiseId: 1 },
  { storeNumber: '003', starbucksStoreNumber: '003', storeName: 'Burger Palace - Airport', storeGroup: 'Transit', legalEntity: '100', franchiseId: 1 },
  // Convenience Store
  { storeNumber: '004', starbucksStoreNumber: '004', storeName: 'QuickStop Express', storeGroup: 'Convenience', legalEntity: '100', franchiseId: 1 },
  // Global Foods Distribution retailers
  { storeNumber: '100', starbucksStoreNumber: '100', storeName: 'Fresh Mart UK', storeGroup: 'UK Retailers', legalEntity: '200', franchiseId: 2 },
  { storeNumber: '101', starbucksStoreNumber: '101', storeName: 'Euro Grocers FR', storeGroup: 'EU Retailers', legalEntity: '201', franchiseId: 2 },
  // Vapiano (Italian Fast-Casual) stores
  { storeNumber: 'VAP001', starbucksStoreNumber: '', storeName: 'Vapiano - Great Portland Street', storeGroup: 'London', legalEntity: '300', franchiseId: 10 },
  { storeNumber: 'VAP002', starbucksStoreNumber: '', storeName: 'Vapiano - Bankside', storeGroup: 'London', legalEntity: '300', franchiseId: 10 },
  { storeNumber: 'VAP003', starbucksStoreNumber: '', storeName: 'Vapiano - Manchester Deansgate', storeGroup: 'North West', legalEntity: '300', franchiseId: 10 },
  // Vapiano Distribution
  { storeNumber: 'VAP100', starbucksStoreNumber: '', storeName: 'Vapiano Distribution UK', storeGroup: 'Distribution', legalEntity: '301', franchiseId: 10 },
  // Five Guys (Burgers & Fries) stores
  { storeNumber: 'FG001', starbucksStoreNumber: '', storeName: 'Five Guys - Covent Garden', storeGroup: 'London', legalEntity: '400', franchiseId: 11 },
  { storeNumber: 'FG002', starbucksStoreNumber: '', storeName: 'Five Guys - Birmingham Bullring', storeGroup: 'West Midlands', legalEntity: '400', franchiseId: 11 },
  { storeNumber: 'FG003', starbucksStoreNumber: '', storeName: 'Five Guys - Leeds Trinity', storeGroup: 'Yorkshire', legalEntity: '400', franchiseId: 11 },
  // Five Guys Distribution
  { storeNumber: 'FG100', starbucksStoreNumber: '', storeName: 'Five Guys Supply UK', storeGroup: 'Distribution', legalEntity: '401', franchiseId: 11 },
];

const mockPersona: Persona = {
  id: 1,
  description: 'Super Admin',
  franchiseName: undefined,
  personaRoles: mockPermissions.filter((p): p is string => p !== null),
  modules: [],
  users: [],
};

const mockCurrentUser: User = {
  id: 1,
  emailAddress: 'admin@orderly.io',
  userLevel: 'FranchiseUser',
  franchiseName: 'Orderly',
  persona: mockPersona,
  stores: mockStores,
  franchises: mockFranchises,
  systems: mockSystems,
  district: undefined,
  allStores: true,
  permissions: mockPermissions.filter((p): p is string => p !== null),
};

const mockUsers: User[] = [mockCurrentUser];

/* ---- Mocked Client Implementation ---- */

import { IUsersClient } from '../../interfaces';
export class UsersClientMocked implements IUsersClient {
  constructor(baseUrl?: string) {}

  async usersPOST(body?: CreateUserRequestDto, cancelToken?: CancelToken): Promise<void> {
    await delay();
    if (body) {
      mockUsers.push({
        id: mockUsers.length + 1,
        emailAddress: body.emailAddress || 'new@user.com',
        userLevel: 'Store',
        franchiseName: body.franchiseName || 'Demo',
        persona: { id: 1, description: 'Store Staff' },
        stores: [],
        franchises: [],
        systems: [],
        district: { id: 1, description: 'Central District' },
        allStores: false,
        permissions: [],
      });
    }
  }

  async usersGET(cancelToken?: CancelToken): Promise<SuccessResponse_1OfUserListResponse> {
    await delay();
    return successResponse({ users: mockUsers }) as SuccessResponse_1OfUserListResponse;
  }

  async activate(body?: ActivateUserRequestDto, cancelToken?: CancelToken): Promise<void> {
    await delay();
  }

  async migrate(body?: MigrateUserRequestDto, cancelToken?: CancelToken): Promise<MigrateUserResponseDto> {
    await delay();
    return {
      success: true,
      message: 'User migrated successfully',
    } as MigrateUserResponseDto;
  }

  async authRequirements(emailAddress?: string, cancelToken?: CancelToken): Promise<AuthRequirementsResponseDto> {
    await delay();
    return {
      requiresPassword: true,
      requiresMfa: false,
      ssoProvider: null,
    } as AuthRequirementsResponseDto;
  }

  async usersPUT(id: number, body?: PutUserRequest, cancelToken?: CancelToken): Promise<void> {
    await delay();
    const user = mockUsers.find((u) => u.id === id);
    if (user && body) {
      user.emailAddress = body.emailAddress || user.emailAddress;
    }
  }

  async usersDELETE(id: number, cancelToken?: CancelToken): Promise<void> {
    await delay();
    const index = mockUsers.findIndex((u) => u.id === id);
    if (index > -1) {
      mockUsers.splice(index, 1);
    }
  }

  async usersGET2(id: number, cancelToken?: CancelToken): Promise<SuccessResponse_1OfUser> {
    await delay();
    const user = mockUsers.find((u) => u.id === id) || mockUsers[0];
    return successResponse(user) as SuccessResponse_1OfUser;
  }

  async resetpassword(body?: ResetPasswordRequestDto, cancelToken?: CancelToken): Promise<void> {
    await delay();
  }

  async import3(body?: ImportUserRequest[], cancelToken?: CancelToken): Promise<void> {
    await delay();
  }

  async franchisename2(franchiseName?: string, cancelToken?: CancelToken): Promise<SuccessResponse_1OfUserListResponse> {
    await delay();
    const users = franchiseName
      ? mockUsers.filter((u) => u.franchiseName === franchiseName)
      : mockUsers;
    return successResponse({ users }) as SuccessResponse_1OfUserListResponse;
  }

  async currentuser(cancelToken?: CancelToken): Promise<SuccessResponse_1OfUser> {
    await delay();
    // Return the comprehensive mock current user with franchises, systems, and permissions
    return successResponse(mockCurrentUser) as SuccessResponse_1OfUser;
  }

  async getStoreUsersWithRole(role: Roles, storeNumber: string, cancelToken?: CancelToken): Promise<SuccessResponse_1OfList_1OfUser> {
    await delay();
    const users = mockUsers.filter((u) =>
      u.stores?.some((s) => s.storeNumber === storeNumber)
    );
    return successResponse(users) as SuccessResponse_1OfList_1OfUser;
  }

  // ====== Auto-generated stub methods ======







}
